import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdMoreVert, MdPrint, MdDownload } from 'react-icons/md';
import Dropdown from '@/components/ui/Dropdown';
import Dialog from '@/components/ui/Dialog';
import Button from '@/components/ui/Button';
import ConfirmDialog from '@/components/shared/ConfirmDialog';
import { triggerErrorMessage } from '@/components/ui/toast';
import { useAppLogger } from '@/observability/useAppLogger';
import { exportFilename, prepareNoteHtml } from './prepareNoteHtml';
import { downloadNoteHtml, openPrintWindow, printNoteHtml } from './noteExportOutput';
import { throwIfAborted } from './exportImages';
import type { ExportMode, ExportProgress, NoteExportSnapshot } from './types';

interface NoteExportMenuProps {
  noteId: string | null;
  snapshot: NoteExportSnapshot | null;
  unavailableReason?: string;
}

interface Operation {
  mode: ExportMode;
  snapshot: NoteExportSnapshot;
  controller: AbortController;
  popup: Window | null;
  settleMissing?: (accepted: boolean) => void;
  started?: boolean;
}

const NoteExportMenu: React.FC<NoteExportMenuProps> = ({ noteId, snapshot, unavailableReason }) => {
  const { t } = useTranslation();
  const { logError } = useAppLogger();
  const operationRef = useRef<Operation | null>(null);
  const [phase, setPhase] = useState<'idle' | 'consent' | 'preparing' | 'missing'>('idle');
  const [progress, setProgress] = useState<ExportProgress>({ done: 0, total: 0 });
  const [missing, setMissing] = useState<string[]>([]);

  const cancel = () => {
    const operation = operationRef.current;
    operationRef.current = null;
    operation?.controller.abort();
    operation?.settleMissing?.(false);
    if (operation?.popup && !operation.popup.closed) operation.popup.close();
    setPhase('idle');
  };

  useEffect(() => {
    setPhase('idle');
    return () => {
      const operation = operationRef.current;
      operationRef.current = null;
      operation?.controller.abort();
      operation?.settleMissing?.(false);
      if (operation?.popup && !operation.popup.closed) operation.popup.close();
    };
  }, [noteId]);

  useEffect(() => {
    if (unavailableReason) cancel();
  }, [unavailableReason]);

  const run = async (operation: Operation) => {
    if (operation.started) return;
    operation.started = true;
    const { signal } = operation.controller;
    let closedMonitor: number | undefined;
    try {
      if (operation.mode === 'print') {
        operation.popup = openPrintWindow(t('noteExport.preparing'));
        if (!operation.popup) {
          triggerErrorMessage(t('noteExport.popupBlocked'));
          return;
        }
        closedMonitor = window.setInterval(() => {
          if (operation.popup?.closed && operationRef.current === operation) cancel();
        }, 250);
      }
      setPhase('preparing');
      setProgress({ done: 0, total: 0 });
      const result = await prepareNoteHtml(operation.snapshot, {
        untitled: t('noteExport.untitled'),
        historicalVersion: t('noteExport.version', { version: operation.snapshot.version }),
        secret: t('noteExport.secret'),
        missingImage: t('noteExport.missingImage'),
      }, signal, setProgress);
      throwIfAborted(signal);
      if (result.missingImages.length > 0) {
        setMissing(result.missingImages);
        setPhase('missing');
        const accepted = await new Promise<boolean>((resolve) => { operation.settleMissing = resolve; });
        operation.settleMissing = undefined;
        if (!accepted) return;
      }
      throwIfAborted(signal);
      setPhase('preparing');
      if (operation.popup) await printNoteHtml(operation.popup, result.html, signal);
      else downloadNoteHtml(result.html, exportFilename(operation.snapshot.title, t('noteExport.untitled')));
      // The completed print document stays open for manual retry or another print.
      operation.popup = null;
    } catch (error: unknown) {
      if (!signal.aborted) {
        logError('Note document preparation failed', { action: operation.mode });
        triggerErrorMessage(t('noteExport.failed'));
      }
    } finally {
      if (closedMonitor !== undefined) window.clearInterval(closedMonitor);
      if (operation.popup && !operation.popup.closed) operation.popup.close();
      if (operationRef.current === operation) {
        operationRef.current = null;
        setPhase('idle');
      }
    }
  };

  const start = (mode: ExportMode) => {
    if (!snapshot || operationRef.current) return;
    const operation: Operation = {
      mode,
      snapshot: { ...snapshot },
      controller: new AbortController(),
      popup: null,
    };
    operationRef.current = operation;
    if (snapshot.encrypted) setPhase('consent');
    else void run(operation);
  };

  const confirm = () => {
    const operation = operationRef.current;
    if (operation) void run(operation);
  };

  const busy = phase !== 'idle';
  return (
    <>
      <div title={unavailableReason} className="shrink-0">
        <Dropdown
          placement="bottom-end"
          renderTitle={<span aria-label={t('noteExport.menu')} title={t('noteExport.menu')}><MdMoreVert size={20} /></span>}
          toggleClassName="rounded p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <Dropdown.Item eventKey="print" disabled={!snapshot || busy} onClick={() => start('print')} className="gap-2">
            <MdPrint />{t('noteExport.print')}
          </Dropdown.Item>
          <Dropdown.Item eventKey="html" disabled={!snapshot || busy} onClick={() => start('html')} className="gap-2">
            <MdDownload />{t('noteExport.download')}
          </Dropdown.Item>
          {unavailableReason && <Dropdown.Item variant="header"><p className="max-w-64 px-2 text-sm">{unavailableReason}</p></Dropdown.Item>}
        </Dropdown>
      </div>
      <ConfirmDialog
        isOpen={phase === 'consent'}
        type="warning"
        title={t('noteExport.plaintextTitle')}
        confirmText={t('noteExport.continue')}
        cancelText={t('noteExport.cancel')}
        onConfirm={confirm}
        onCancel={cancel}
        onClose={cancel}
        onRequestClose={cancel}
      >
        <p>{t('noteExport.plaintextWarning')}</p>
      </ConfirmDialog>
      <ConfirmDialog
        isOpen={phase === 'missing'}
        type="warning"
        title={t('noteExport.missingTitle')}
        confirmText={t('noteExport.withPlaceholders')}
        cancelText={t('noteExport.cancel')}
        onConfirm={() => operationRef.current?.settleMissing?.(true)}
        onCancel={cancel}
        onClose={cancel}
        onRequestClose={cancel}
      >
        <p>{t('noteExport.missingWarning')}</p>
        <ul className="my-2 max-h-40 overflow-auto list-disc pl-5">
          {missing.map((label, index) => <li key={index}>{label}</li>)}
        </ul>
      </ConfirmDialog>
      <Dialog
        isOpen={phase === 'preparing'}
        onClose={cancel}
        onRequestClose={cancel}
        contentLabel={t('noteExport.preparing')}
      >
        <h2 className="text-lg font-semibold">{t('noteExport.preparing')}</h2>
        <p className="my-4" role="status">
          {progress.total ? t('noteExport.progress', { ...progress }) : t('noteExport.preparing')}
        </p>
        <Button onClick={cancel}>{t('noteExport.cancel')}</Button>
      </Dialog>
    </>
  );
};

export default NoteExportMenu;
